/**
 * Local-file (file://) access: the copy and the detection shared by the
 * extension, the dashboard onboarding wizard and the help corpus contract.
 *
 * Chrome blocks every extension from file:// pages until the user turns on the
 * per-extension "Allow access to file URLs" setting on chrome://extensions
 * (developer.chrome.com: chrome.extension.isAllowedFileSchemeAccess "corresponds
 * to the user-controlled per-extension 'Allow access to File URLs' setting").
 * Measured 2026-09-02: a new user clicked the toolbar icon 24 times on a local
 * HTML file, every click was refused with nothing shown, the onboarding wizard
 * had stored the file path as a website, and the help chat told him the
 * extension could not work on local files. Three surfaces, three stories.
 * This module is the one place the setting is named, so they cannot drift.
 *
 * No em dashes: this is user-facing copy (project copy rule).
 */
/** Chrome's own label for the toggle, verbatim. */
export const FILE_URL_ACCESS_SETTING = 'Allow access to file URLs';
/**
 * Toolbar-icon tooltip set on the file:// tab whose click was refused
 * (background chrome.action.setTitle). The page itself can host no UI of ours
 * while the setting is off, so the icon is the only surface on that tab. Since
 * 2026-10-31 the click opens the extension's own help page (below) rather than
 * chrome://extensions, so the tooltip points at that tab.
 */
export const FILE_URL_ACCESS_ACTION_TITLE = `OverlayQA: turn on "${FILE_URL_ACCESS_SETTING}" in Chrome's extension settings. The OverlayQA tab that just opened shows how.`;
/**
 * The help page a refused file:// click opens (extension src/file-access/).
 * One source for the page, the help corpus and the e2e proof. Measured
 * 2026-10-31 on the store build: the direct chrome://extensions open put the
 * toggle below the fold with nothing on screen saying why the page had opened,
 * and a second user hit two refusals four seconds apart on 1.2.101.
 */
export const FILE_URL_ACCESS_HELP = {
    // One line at the card's 28rem width (ModalTitle headings never wrap, Emily 2026-10-26).
    title: 'Allow OverlayQA to open local files',
    body: 'Chrome keeps extensions off files opened from your computer until you allow it. Only you can turn this on; OverlayQA cannot do it for you.',
    steps: [
        "Open OverlayQA's Chrome settings.",
        `Turn on "${FILE_URL_ACCESS_SETTING}".`,
        'Come back to your file. OverlayQA opens on it.',
    ],
    openSettings: 'Open Chrome settings',
    returnToFile: 'Return to your file',
    statusOff: 'Access is off',
    statusOn: 'Access is on',
    fileGone: 'Your file is no longer open in a tab. Open it again in Chrome and click the OverlayQA icon.',
};
/**
 * Sidebar toast the first time it opens on a file:// page after the guidance
 * handoff. The final two words are bound with a non-breaking space so the
 * toast cannot strand one word on its last line (house typography rule).
 */
export const FILE_URL_ACCESS_ENABLED_TOAST = 'Local file access is on. OverlayQA now works on files you open in\u00a0Chrome.';
/**
 * Onboarding "Website URL" inline message when the typed value is a path to a
 * file on the user's computer rather than a site. Blocks Finish the same way
 * an invalid URL does: the server cannot reach a file, and https-prefixing the
 * path used to store "https://file///C:/..." as a project URL.
 */
export const ONBOARDING_LOCAL_FILE_MESSAGE = `That is a file on your computer, not a website. Enter a site URL here or pick a demo. To review the file itself, install the extension, turn on "${FILE_URL_ACCESS_SETTING}" on chrome://extensions, and open the file in\u00a0Chrome.`;
/**
 * Is this typed value a file on the user's own machine? Recognises the file:
 * scheme, a Windows drive path, a home-relative path and a POSIX absolute
 * path. A host that merely CONTAINS a drive-like segment ("acme.io/c:/x") is a
 * site, not a file.
 */
export function isLocalFilePath(value) {
    const v = value.trim();
    if (!v)
        return false;
    if (/^file:/i.test(v))
        return true;
    if (/^[a-z]:[\\/]/i.test(v))
        return true;
    if (/^~[\\/]/.test(v))
        return true;
    if (/^\/(?!\/)/.test(v))
        return true;
    return false;
}
//# sourceMappingURL=fileUrlAccess.js.map